import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Answers, Axis, AxisAnswer, BoardAnswer } from '@cf/shared'
import { reviewApi } from '@/lib/api'

export type FlatBoard = {
  board: Axis['boards'][number]
  axis: Axis
  /** Index in the whole report, which is the order of the stream. */
  index: number
}

export function useInvite(token: string) {
  return useQuery({
    queryKey: ['invite', token],
    queryFn: () => reviewApi.invite(token),
  })
}

export function useReport(token: string) {
  return useQuery({
    queryKey: ['report', token],
    queryFn: () => reviewApi.report(token),
    staleTime: Infinity,
  })
}

export function useAnswers(token: string) {
  return useQuery({
    queryKey: ['answers', token],
    queryFn: () => reviewApi.answers(token),
  })
}

export function flatten(axes: Axis[]): FlatBoard[] {
  const flat: FlatBoard[] = []
  for (const axis of axes) {
    for (const board of axis.boards) {
      flat.push({ board, axis, index: flat.length })
    }
  }
  return flat
}

/**
 * Saving is a write-through cache: the answer lands in the query cache first
 * so the chip is filled before the round trip, and the request follows. A
 * failed save rolls the cache back and the toast says so, because silently
 * keeping a value the server never got is the one behaviour that would waste
 * a reviewer's evening.
 */
export function useSaveBoardAnswer(token: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ boardId, answer }: { boardId: string; answer: BoardAnswer }) =>
      reviewApi.saveBoard(token, boardId, answer),
    onMutate: async ({ boardId, answer }) => {
      await client.cancelQueries({ queryKey: ['answers', token] })
      const previous = client.getQueryData<Answers>(['answers', token])
      client.setQueryData<Answers>(['answers', token], (current) =>
        current
          ? { ...current, boards: { ...current.boards, [boardId]: answer } }
          : current,
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        client.setQueryData(['answers', token], context.previous)
      }
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['invite', token] })
    },
  })
}

export function useSaveAxisAnswer(token: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ axisId, answer }: { axisId: string; answer: AxisAnswer }) =>
      reviewApi.saveAxis(token, axisId, answer),
    onMutate: async ({ axisId, answer }) => {
      await client.cancelQueries({ queryKey: ['answers', token] })
      const previous = client.getQueryData<Answers>(['answers', token])
      client.setQueryData<Answers>(['answers', token], (current) =>
        current ? { ...current, axes: { ...current.axes, [axisId]: answer } } : current,
      )
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        client.setQueryData(['answers', token], context.previous)
      }
    },
  })
}

/**
 * A board counts as done once the description has a verdict.
 *
 * On an A/B board the `accuracy` question is not asked; the server derives it
 * as the best of the per-wording verdicts at save time. The optimistic cache
 * entry carries only `variants` — there is no refetch after a save — so the
 * derivation has to be mirrored here, or the progress bar and the "saved"
 * note freeze until the next full reload.
 */
export function isAnswered(answer: BoardAnswer | undefined): boolean {
  if (answer?.accuracy) return true
  return Object.values(answer?.variants ?? {}).some(Boolean)
}
